import React, { useState, useMemo } from 'react';
import USAMap from 'react-usa-map';
import './USAMapStyles.css';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Tooltip,
  useTheme,
  useMediaQuery,
  Stack,
  Paper,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Room as LocationIcon,
  Visibility as ViewIcon,
  TouchApp as ClickIcon,
  TrendingUp,
  Public,
  Star,
  Email as EmailIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

/**
 * Geographic Map View with Interactive USA SVG Map
 * Shows email opens/clicks by US State with clickable map
 */
const GeographicMapView = ({ trackingData = [] }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [selectedState, setSelectedState] = useState(null);
  const [hoveredState, setHoveredState] = useState(null);

  // US States mapping with full names
  const US_STATES = {
    'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas', 'CA': 'California',
    'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware', 'FL': 'Florida', 'GA': 'Georgia',
    'HI': 'Hawaii', 'ID': 'Idaho', 'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa',
    'KS': 'Kansas', 'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
    'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi', 'MO': 'Missouri',
    'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada', 'NH': 'New Hampshire', 'NJ': 'New Jersey',
    'NM': 'New Mexico', 'NY': 'New York', 'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio',
    'OK': 'Oklahoma', 'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
    'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah', 'VT': 'Vermont',
    'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia', 'WI': 'Wisconsin', 'WY': 'Wyoming',
  };

  // Extract state from tracking data
  const extractState = (email) => {
    const locationData = email.locationData || email.state || email.metadata?.state;
    if (locationData && US_STATES[locationData.toUpperCase()]) {
      return locationData.toUpperCase();
    }
    
    if (email.recipientState) {
      return email.recipientState.toUpperCase();
    }

    return null;
  };

  // Calculate statistics by state
  const stateStats = useMemo(() => {
    const stats = {};
    
    trackingData.forEach(email => {
      const state = extractState(email);
      if (!state || !US_STATES[state]) return;
      
      if (!stats[state]) {
        stats[state] = {
          state,
          name: US_STATES[state],
          opens: 0,
          clicks: 0,
          sent: 0,
          uniqueRecipients: new Set(),
        };
      }
      
      stats[state].sent++;
      stats[state].uniqueRecipients.add(email.recipient || email.email);
      
      if (email.opened) stats[state].opens++;
      if (email.clicked) stats[state].clicks++;
    });
    
    return Object.values(stats).map(stat => ({
      ...stat,
      uniqueRecipients: stat.uniqueRecipients.size,
      openRate: stat.sent > 0 ? ((stat.opens / stat.sent) * 100).toFixed(1) : 0,
      clickRate: stat.sent > 0 ? ((stat.clicks / stat.sent) * 100).toFixed(1) : 0,
    })).sort((a, b) => b.sent - a.sent);
  }, [trackingData]);

  // Get max values for color scaling
  const maxSent = Math.max(...stateStats.map(s => s.sent), 1);

  // Get state data by abbreviation
  const getStateData = (stateAbbr) => {
    return stateStats.find(s => s.state === stateAbbr);
  };

  // Get color based on activity level
  const getStateColor = (stateAbbr) => {
    const data = getStateData(stateAbbr);
    if (!data) return '#e2e8f0'; // Light gray for no data
    
    const percentage = (data.sent / maxSent) * 100;
    if (percentage >= 75) return '#10b981'; // Green - High
    if (percentage >= 50) return '#f59e0b'; // Orange - Medium
    if (percentage >= 25) return '#3b82f6'; // Blue - Low
    return '#94a3b8'; // Gray - Very low
  };

  const topStates = stateStats.slice(0, 5);

  // Handle state click
  const handleStateClick = (stateAbbr) => {
    const data = getStateData(stateAbbr);
    if (data) {
      setSelectedState(data);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Header */}
      <Card
        variant="outlined"
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          borderRadius: 2,
        }}
      >
        <CardContent sx={{ p: isMobile ? 2 : 3 }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Public sx={{ fontSize: 40 }} />
            <Box flex={1}>
              <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight={700}>
                🗺️ Geographic Email Performance Map
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.95, mt: 0.5 }}>
                Visual representation of email engagement across United States
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Stats Overview */}
      <Grid container spacing={2}>
        <Grid item xs={6} md={3}>
          <Paper 
            elevation={0}
            sx={{ 
              p: 2.5, 
              textAlign: 'center', 
              background: 'linear-gradient(135deg, #667eea15, #764ba215)',
              border: '1px solid #667eea30',
              borderRadius: 2,
            }}
          >
            <Typography variant="h3" fontWeight={700} color="#667eea">
              {stateStats.length}
            </Typography>
            <Typography variant="body2" color="text.secondary" fontWeight={600}>
              States Reached
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={3}>
          <Paper 
            elevation={0}
            sx={{ 
              p: 2.5, 
              textAlign: 'center', 
              background: 'linear-gradient(135deg, #10b98115, #05966915)',
              border: '1px solid #10b98130',
              borderRadius: 2,
            }}
          >
            <Typography variant="h3" fontWeight={700} color="#10b981">
              {stateStats.reduce((sum, s) => sum + s.opens, 0).toLocaleString()}
            </Typography>
            <Typography variant="body2" color="text.secondary" fontWeight={600}>
              Total Opens
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={3}>
          <Paper 
            elevation={0}
            sx={{ 
              p: 2.5, 
              textAlign: 'center', 
              background: 'linear-gradient(135deg, #3b82f615, #2563eb15)',
              border: '1px solid #3b82f630',
              borderRadius: 2,
            }}
          >
            <Typography variant="h3" fontWeight={700} color="#3b82f6">
              {stateStats.reduce((sum, s) => sum + s.clicks, 0).toLocaleString()}
            </Typography>
            <Typography variant="body2" color="text.secondary" fontWeight={600}>
              Total Clicks
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={3}>
          <Paper 
            elevation={0}
            sx={{ 
              p: 2.5, 
              textAlign: 'center', 
              background: 'linear-gradient(135deg, #f59e0b15, #d9770615)',
              border: '1px solid #f59e0b30',
              borderRadius: 2,
            }}
          >
            <Typography variant="h3" fontWeight={700} color="#f59e0b" fontSize={isMobile ? '1.5rem' : '2rem'}>
              {topStates[0]?.state || 'N/A'}
            </Typography>
            <Typography variant="body2" color="text.secondary" fontWeight={600}>
              Top State
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Interactive USA Map */}
      <Card variant="outlined">
        <CardContent sx={{ p: isMobile ? 2 : 3 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
            <Typography variant="h6" fontWeight={700}>
              🌎 United States Engagement Map
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                size="small"
                icon={<ViewIcon />}
                label="Click states for details"
                sx={{ fontWeight: 600 }}
              />
            </Stack>
          </Stack>
          
          {stateStats.length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              📊 No geographic data available yet. Send emails to see state-based engagement!
            </Alert>
          ) : (
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                borderRadius: 2,
                border: '2px solid #cbd5e1',
                overflow: 'hidden',
                p: isMobile ? 2 : 4,
              }}
            >
              {/* Real USA Map */}
              <Box 
                className="usa-map-container"
                sx={{ 
                  '& path': {
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    stroke: '#fff',
                    strokeWidth: 1.5,
                  },
                  '& path:hover': {
                    opacity: 0.8,
                    transform: 'scale(1.02)',
                    filter: 'brightness(1.1)',
                  },
                }}
              >
                <USAMap
                  customize={Object.keys(US_STATES).reduce((acc, stateAbbr) => {
                    const data = getStateData(stateAbbr);
                    acc[stateAbbr] = {
                      fill: getStateColor(stateAbbr),
                      clickHandler: (event) => handleStateClick(stateAbbr),
                    };
                    return acc;
                  }, {})}
                  onClick={(event) => {
                    const stateAbbr = event.target.dataset.name;
                    if (stateAbbr) handleStateClick(stateAbbr);
                  }}
                  width={isMobile ? 300 : isTablet ? 600 : 900}
                  height={isMobile ? 200 : isTablet ? 400 : 600}
                />
              </Box>

              {/* Legend */}
              <Stack 
                direction={isMobile ? 'column' : 'row'} 
                spacing={2} 
                mt={3} 
                justifyContent="center"
                flexWrap="wrap"
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box sx={{ width: 24, height: 24, bgcolor: '#10b981', borderRadius: 0.5 }} />
                  <Typography variant="caption" fontWeight={600}>High Activity (75%+)</Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box sx={{ width: 24, height: 24, bgcolor: '#f59e0b', borderRadius: 0.5 }} />
                  <Typography variant="caption" fontWeight={600}>Medium (50-75%)</Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box sx={{ width: 24, height: 24, bgcolor: '#3b82f6', borderRadius: 0.5 }} />
                  <Typography variant="caption" fontWeight={600}>Low (25-50%)</Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box sx={{ width: 24, height: 24, bgcolor: '#94a3b8', borderRadius: 0.5 }} />
                  <Typography variant="caption" fontWeight={600}>Very Low (&lt;25%)</Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box sx={{ width: 24, height: 24, bgcolor: '#e2e8f0', borderRadius: 0.5, border: '1px solid #cbd5e1' }} />
                  <Typography variant="caption" fontWeight={600}>No Data</Typography>
                </Stack>
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Top 5 States Table */}
      {topStates.length > 0 && (
        <Card variant="outlined">
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
              <Star sx={{ color: '#fbbf24', fontSize: 28 }} />
              <Typography variant="h6" fontWeight={700}>
                🏆 Top 5 Performing States
              </Typography>
            </Stack>
            
            <TableContainer>
              <Table size={isMobile ? 'small' : 'medium'}>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Rank</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>State</TableCell>
                    {!isMobile && <TableCell align="right" sx={{ fontWeight: 700 }}>Sent</TableCell>}
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Opens</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Open %</TableCell>
                    {!isMobile && <TableCell align="right" sx={{ fontWeight: 700 }}>Clicks</TableCell>}
                    {!isMobile && <TableCell align="center" sx={{ fontWeight: 700 }}>Performance</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topStates.map((stat, index) => (
                    <TableRow
                      key={stat.state}
                      onClick={() => setSelectedState(stat)}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': { bgcolor: '#f1f5f9' },
                        bgcolor: selectedState?.state === stat.state ? '#e0f2fe' : 'transparent',
                      }}
                    >
                      <TableCell>
                        <Chip
                          label={`#${index + 1}`}
                          size="small"
                          sx={{
                            bgcolor: index === 0 ? '#fbbf24' : index === 1 ? '#94a3b8' : '#c4b5fd',
                            color: 'white',
                            fontWeight: 700,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <LocationIcon fontSize="small" sx={{ color: getStateColor(stat.state) }} />
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {stat.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {stat.state}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      {!isMobile && (
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={600}>
                            {stat.sent}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell align="right">
                        <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={0.5}>
                          <ViewIcon fontSize="small" sx={{ color: '#10b981' }} />
                          <Typography variant="body2">{stat.opens}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={`${stat.openRate}%`}
                          size="small"
                          sx={{
                            bgcolor: parseFloat(stat.openRate) >= 30 ? '#10b98120' : '#6b728020',
                            color: parseFloat(stat.openRate) >= 30 ? '#10b981' : '#6b7280',
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      {!isMobile && (
                        <>
                          <TableCell align="right">
                            <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={0.5}>
                              <ClickIcon fontSize="small" sx={{ color: '#3b82f6' }} />
                              <Typography variant="body2">{stat.clicks}</Typography>
                            </Stack>
                          </TableCell>
                          <TableCell align="center">
                            <LinearProgress
                              variant="determinate"
                              value={Math.min(parseFloat(stat.openRate) * 2, 100)}
                              sx={{
                                height: 8,
                                borderRadius: 4,
                                bgcolor: '#e5e7eb',
                                '& .MuiLinearProgress-bar': {
                                  background: `linear-gradient(90deg, ${getStateColor(stat.state)}, ${getStateColor(stat.state)}80)`,
                                },
                              }}
                            />
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Selected State Details */}
      {selectedState && (
        <Card
          elevation={4}
          sx={{
            border: `3px solid ${getStateColor(selectedState.state)}`,
            background: `linear-gradient(135deg, ${getStateColor(selectedState.state)}15, ${getStateColor(selectedState.state)}05)`,
          }}
        >
          <CardContent>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <LocationIcon sx={{ fontSize: 36, color: getStateColor(selectedState.state) }} />
                <Box>
                  <Typography variant="h5" fontWeight={700}>
                    {selectedState.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedState.state} • Detailed Performance Metrics
                  </Typography>
                </Box>
              </Stack>
              <IconButton onClick={() => setSelectedState(null)} size="small">
                <CloseIcon />
              </IconButton>
            </Stack>

            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f8fafc' }}>
                  <EmailIcon sx={{ color: '#667eea', fontSize: 32, mb: 1 }} />
                  <Typography variant="h4" fontWeight={700}>
                    {selectedState.sent}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Emails Sent
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f8fafc' }}>
                  <ViewIcon sx={{ color: '#10b981', fontSize: 32, mb: 1 }} />
                  <Typography variant="h4" fontWeight={700}>
                    {selectedState.opens}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Opens ({selectedState.openRate}%)
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f8fafc' }}>
                  <ClickIcon sx={{ color: '#3b82f6', fontSize: 32, mb: 1 }} />
                  <Typography variant="h4" fontWeight={700}>
                    {selectedState.clicks}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Clicks ({selectedState.clickRate}%)
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f8fafc' }}>
                  <TrendingUp sx={{ color: '#f59e0b', fontSize: 32, mb: 1 }} />
                  <Typography variant="h4" fontWeight={700}>
                    {selectedState.uniqueRecipients}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Unique Recipients
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default GeographicMapView;
